import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";
import OrderTypeBadge from "@/components/OrderTypeBadge";
import { toast } from "sonner";
import { Eye, Pencil, Plus, Trash2, Download } from "lucide-react";
import SkuCombobox from "@/components/SkuCombobox";

function getClaimsTitle(role) {
  if (role === "approval") return "Claim Approvals";
  if (role === "sales") return "My Claim Orders";
  return "All Claim Orders";
}

export default function Claims() {
  const { user } = useAuth();
  const [claims, setClaims] = useState([]);
  const [status, setStatus] = useState("all");
  const [agentId, setAgentId] = useState("all");
  const [platform, setPlatform] = useState("all");
  const [platforms, setPlatforms] = useState([]);
  const [agents, setAgents] = useState([]);
  const [detail, setDetail] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // ----- Edit-claim state (BDM/admin can edit while status is Pending) -----
  const [editing, setEditing] = useState(null); // full claim being edited
  const [editForm, setEditForm] = useState({
    customer_name: "", phone: "", order_amount: 0, platform: "", notes: "",
  });
  const [editItems, setEditItems] = useState([]);
  const [editSaving, setEditSaving] = useState(false);
  const [skus, setSkus] = useState([]);

  const canEditRow = (row) => row.status === "Pending" && (user.role === "sales" || user.role === "admin");
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const PAGE = 100;

  const canFilterByAgent = user.role === "approval" || user.role === "admin";
  // Source filter is intended for the approval team (and admin). Sales/subadmin
  // only see their own claims so slicing by source there is low-value.
  const canFilterBySource = user.role === "approval" || user.role === "admin";
  const canFilterByDate = user.role === "approval" || user.role === "admin";
  // Claims that are still actionable by the approval team.
  const isEditable = (s) => s === "Pending" || s === "In Transit";

  const load = useCallback(async (append = false) => {
    const params = { limit: PAGE, offset: append ? claims.length : 0 };
    if (status !== "all") params.status = status;
    if (canFilterByAgent && agentId !== "all") params.agent_id = agentId;
    if (canFilterBySource && platform !== "all") params.platform = platform;
    if (canFilterByDate && startDate) params.start_date = startDate;
    if (canFilterByDate && endDate) params.end_date = endDate;
    setLoading(true);
    try {
      const countParams = { ...params };
      delete countParams.limit;
      delete countParams.offset;
      const [{ data }, { data: cnt }] = await Promise.all([
        api.get("/claims", { params }),
        append ? Promise.resolve({ data: { total } }) : api.get("/claims/count", { params: countParams }),
      ]);
      setClaims((prev) => (append ? [...prev, ...data] : data));
      if (!append) setTotal(cnt.total || 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [status, agentId, platform, startDate, endDate, canFilterByAgent, canFilterBySource, canFilterByDate, claims.length, total]);

  useEffect(() => { load(false); }, [status, agentId, platform, startDate, endDate]);

  useEffect(() => {
    if (!canFilterByAgent) return;
    api.get("/users/agents").then(({ data }) => setAgents(data)).catch(() => {});
  }, [canFilterByAgent]);

  useEffect(() => {
    if (!canFilterBySource) return;
    api.get("/claims/platforms")
      .then(({ data }) => setPlatforms(data.platforms || []))
      .catch(() => {});
  }, [canFilterBySource]);

  // Load SKUs once (needed for the item editor inside the Edit dialog)
  useEffect(() => {
    api.get("/skus", { params: { active_only: true } })
      .then(({ data }) => setSkus(data))
      .catch(() => {});
  }, []);

  const openEdit = (row) => {
    setEditing(row);
    setEditForm({
      customer_name: row.customer_name || "",
      phone: row.phone || "",
      order_amount: Number(row.order_amount || 0),
      platform: row.platform || "",
      notes: row.notes || "",
    });
    setEditItems(
      (row.selected_skus || []).map((s) => ({
        uid: `${s.sku_id}-${Math.random().toString(36).slice(2, 8)}`,
        sku_id: s.sku_id,
        quantity: Number(s.quantity || 1),
      })),
    );
  };

  const updateEditItem = (uid, patch) => {
    setEditItems((rows) => rows.map((r) => (r.uid === uid ? { ...r, ...patch } : r)));
  };
  const addEditItem = () => {
    setEditItems((rows) => [...rows, { uid: `new-${Date.now()}`, sku_id: "", quantity: 1 }]);
  };
  const removeEditItem = (uid) => {
    setEditItems((rows) => (rows.length <= 1 ? rows : rows.filter((r) => r.uid !== uid)));
  };

  const saveEdit = async () => {
    // Validate items
    const cleaned = editItems
      .filter((r) => r.sku_id && Number(r.quantity) > 0)
      .map((r) => ({ sku_id: r.sku_id, quantity: Number(r.quantity) }));
    if (cleaned.length === 0) {
      toast.error("At least one item with a valid quantity is required");
      return;
    }
    setEditSaving(true);
    try {
      await api.put(`/claims/${editing.id}`, {
        customer_name: editForm.customer_name,
        phone: editForm.phone,
        order_amount: Number(editForm.order_amount || 0),
        platform: editForm.platform,
        notes: editForm.notes,
        items: cleaned,
      });
      toast.success("Claim updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setEditSaving(false);
    }
  };

  const decide = async (decision) => {
    try {
      await api.put(`/claims/${detail.id}/decision`, { status: decision, approval_remarks: remarks });
      toast.success(`Claim ${decision.toLowerCase()}`);
      setDetail(null);
      setRemarks("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const reopen = async () => {
    if (!window.confirm(
      "Reopen this claim for approval team review?\n\n" +
      "The claim will go back to Pending. If it was previously Approved, the " +
      "corresponding claimed order will be removed so a fresh decision can " +
      "recreate it cleanly."
    )) return;
    try {
      await api.post(`/claims/${detail.id}/reopen`);
      toast.success("Claim moved back to Pending — sent to the approval team");
      setDetail(null);
      setRemarks("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Export the currently-filtered claim list to Excel/CSV.
  // Server re-applies role scoping; here we just forward the visible filters.
  const downloadReport = (fmt = "xlsx") => {
    const params = new URLSearchParams({ fmt });
    if (status !== "all") params.set("status", status);
    if (canFilterByAgent && agentId !== "all") params.set("agent_id", agentId);
    if (canFilterBySource && platform !== "all") params.set("platform", platform);
    if (canFilterByDate && startDate) params.set("start_date", startDate);
    if (canFilterByDate && endDate) params.set("end_date", endDate);
    const base = process.env.REACT_APP_BACKEND_URL;
    // Auth is via httpOnly cookies (same as axios's withCredentials).
    fetch(`${base}/api/reports/claims?${params.toString()}`, {
      credentials: "include",
    })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        // Derive filename from server's Content-Disposition
        const cd = r.headers.get("Content-Disposition") || "";
        const m = cd.match(/filename="?([^";]+)"?/i);
        const fname = m ? m[1] : `claims_report.${fmt}`;
        return r.blob().then((b) => ({ b, fname }));
      })
      .then(({ b, fname }) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = url; a.download = fname; a.click();
        URL.revokeObjectURL(url);
      })
      .catch((e) => toast.error(`Download failed: ${e.message || e}`));
  };

  const title = getClaimsTitle(user.role);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-2xl tracking-tight">{title}</h1>
          <p className="text-sm text-muted-foreground">{user.role === "approval" ? "Review and decide on submitted claims" : "Track your claim orders"}</p>
        </div>
        <Button
          variant="outline"
          onClick={() => downloadReport("xlsx")}
          data-testid="claims-download-report"
        >
          <Download className="w-4 h-4 mr-1" /> Download Report
        </Button>
      </div>

      <Card className="p-4">
        <div className="flex flex-wrap gap-3">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="max-w-xs w-48" data-testid="claims-filter"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="Pending">Pending</SelectItem>
              <SelectItem value="Approved">Approved</SelectItem>
              <SelectItem value="Rejected">Rejected</SelectItem>
              <SelectItem value="Undelivered / RTO">Undelivered / RTO</SelectItem>
              <SelectItem value="In Transit">In Transit</SelectItem>
            </SelectContent>
          </Select>
          {canFilterByAgent && (
            <Select value={agentId} onValueChange={setAgentId}>
              <SelectTrigger className="max-w-xs w-60" data-testid="claims-agent-filter"><SelectValue placeholder="BDM" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All BDMs</SelectItem>
                {agents.map((a) => (
                  <SelectItem key={a.id} value={a.id}>{a.name}{a.email ? ` (${a.email})` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {canFilterBySource && (
            <Select value={platform} onValueChange={setPlatform}>
              <SelectTrigger className="max-w-xs w-52" data-testid="claims-source-filter"><SelectValue placeholder="Source" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sources</SelectItem>
                {platforms.map((p) => (
                  <SelectItem key={p} value={p} data-testid={`claims-source-option-${p}`}>{p}</SelectItem>
                ))}
                {platforms.length === 0 && (
                  <SelectItem value="__none__" disabled>No sources yet</SelectItem>
                )}
              </SelectContent>
            </Select>
          )}
          {canFilterByDate && (
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1">
                <Label htmlFor="claims-from" className="text-xs text-muted-foreground whitespace-nowrap">From</Label>
                <Input
                  id="claims-from"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-40"
                  data-testid="claims-start-date"
                />
              </div>
              <div className="flex items-center gap-1">
                <Label htmlFor="claims-to" className="text-xs text-muted-foreground whitespace-nowrap">To</Label>
                <Input
                  id="claims-to"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-40"
                  data-testid="claims-end-date"
                />
              </div>
              {(startDate || endDate) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setStartDate(""); setEndDate(""); }}
                  className="text-xs"
                  data-testid="claims-date-clear"
                >
                  Clear
                </Button>
              )}
            </div>
          )}
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Claim ID</TableHead>
                <TableHead>BDM</TableHead>
                <TableHead>Existing Order</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Platform</TableHead>
                <TableHead>Order Type</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {claims.length === 0 && <TableRow><TableCell colSpan={11} className="text-center text-muted-foreground py-8">No claims</TableCell></TableRow>}
              {claims.map((c) => (
                <TableRow key={c.id} data-testid={`claim-row-${c.claim_id}`}>
                  <TableCell className="font-mono text-xs">{c.claim_id}</TableCell>
                  <TableCell>{c.agent_name}</TableCell>
                  <TableCell className="font-mono text-xs">{c.existing_order_id}</TableCell>
                  <TableCell>{c.customer_name}</TableCell>
                  <TableCell>{c.phone}</TableCell>
                  <TableCell className="text-xs">{c.platform || <span className="text-muted-foreground">—</span>}</TableCell>
                  <TableCell>₹{Number(c.order_amount).toFixed(2)}</TableCell>
                  <TableCell><StatusBadge status={c.status} /></TableCell>
                  <TableCell className="text-xs text-muted-foreground">{new Date(c.created_at).toLocaleString()}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {canEditRow(c) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEdit(c)}
                          title="Edit claim (Pending only)"
                          data-testid={`edit-claim-${c.claim_id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => { setDetail(c); setRemarks(c.approval_remarks || ""); }} data-testid={`view-claim-${c.claim_id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center justify-between p-3 border-t bg-muted/20 text-xs text-muted-foreground">
          <div data-testid="claims-showing">Showing {claims.length} of {total} claim orders</div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => load(true)}
            disabled={loading || claims.length >= total}
            data-testid="claims-load-more"
          >
            {loading ? "Loading…" : claims.length >= total ? "All loaded" : "Load more"}
          </Button>
        </div>
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-xl">
          {detail && (
            <>
              <DialogHeader><DialogTitle>Claim {detail.claim_id}</DialogTitle></DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div><div className="text-xs text-muted-foreground">BDM</div><div>{detail.agent_name}</div></div>
                  <div><div className="text-xs text-muted-foreground">Status</div><StatusBadge status={detail.status} /></div>
                  <div><div className="text-xs text-muted-foreground">Existing Order</div><div className="font-mono">{detail.existing_order_id}</div></div>
                  <div><div className="text-xs text-muted-foreground">Amount</div><div>₹{Number(detail.order_amount).toFixed(2)}</div></div>
                  <div><div className="text-xs text-muted-foreground">Customer</div><div>{detail.customer_name}</div></div>
                  <div><div className="text-xs text-muted-foreground">Phone</div><div>{detail.phone}</div></div>
                  {detail.platform && <div className="col-span-2"><div className="text-xs text-muted-foreground">Platform</div><div>{detail.platform}</div></div>}
                </div>
                {detail.selected_skus && detail.selected_skus.length > 0 && (
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Products in this order</div>
                    <div className="flex flex-wrap gap-1.5" data-testid="claim-detail-skus">
                      {detail.selected_skus.map((s) => (
                        <span key={s.sku_id} className="inline-flex items-center gap-1 bg-muted border border-border text-xs rounded-full px-2.5 py-1">
                          <span>{s.sku_name}</span>
                          {s.quantity != null && <span className="font-medium">× {s.quantity}</span>}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {detail.notes && (
                  <div><div className="text-xs text-muted-foreground">Notes</div><div className="whitespace-pre-wrap">{detail.notes}</div></div>
                )}
                {detail.proof_upload && (
                  <div>
                    <div className="text-xs text-muted-foreground">Proof</div>
                    <a href={`/api/files/${detail.proof_upload}`} target="_blank" rel="noreferrer" className="text-sm underline text-blue-600">
                      View proof
                    </a>
                  </div>
                )}
                {(user.role === "approval" || user.role === "admin") && isEditable(detail.status) && (
                  <div className="border-t border-border pt-3 space-y-2">
                    <Label>Approval remarks</Label>
                    <Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="approval-remarks" />
                    {detail.status === "In Transit" && (
                      <div className="text-xs text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-2 py-1.5">
                        This claim is currently marked <b>In Transit</b>. Approve once the order is delivered, or mark Rejected / Undelivered-RTO once confirmed.
                      </div>
                    )}
                  </div>
                )}
                {!isEditable(detail.status) && detail.approval_remarks && (
                  <div><div className="text-xs text-muted-foreground">Remarks</div><div>{detail.approval_remarks}</div></div>
                )}
              </div>
              <DialogFooter className="flex-wrap gap-2">
                <Button variant="outline" onClick={() => setDetail(null)}>Close</Button>
                {user.role === "admin" && !isEditable(detail.status) && (
                  <Button
                    variant="outline"
                    onClick={reopen}
                    className="border-amber-300 text-amber-700 hover:bg-amber-50"
                    data-testid="reopen-claim-btn"
                  >
                    Reopen for Review
                  </Button>
                )}
                {(user.role === "approval" || user.role === "admin") && isEditable(detail.status) && (
                  <>
                    <Button variant="outline" onClick={() => decide("Rejected")} data-testid="reject-claim-btn">Reject</Button>
                    <Button
                      variant="outline"
                      onClick={() => decide("Undelivered / RTO")}
                      className="border-red-300 text-red-700 hover:bg-red-50"
                      data-testid="rto-claim-btn"
                    >
                      Undelivered / RTO
                    </Button>
                    {detail.status === "Pending" && (
                      <Button
                        variant="outline"
                        onClick={() => decide("In Transit")}
                        className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                        data-testid="in-transit-claim-btn"
                      >
                        In Transit
                      </Button>
                    )}
                    <Button onClick={() => decide("Approved")} data-testid="approve-claim-btn">Approve</Button>
                  </>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit claim dialog — only reachable while status = Pending */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-claim-dialog">
          {editing && (
            <>
              <DialogHeader>
                <DialogTitle>Edit claim · {editing.claim_id}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                <div className="text-xs text-muted-foreground">
                  Only <b>Pending</b> claims can be edited. Once the approval team decides,
                  changes are locked.
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="edit-cust">Customer name</Label>
                    <Input
                      id="edit-cust"
                      value={editForm.customer_name}
                      onChange={(e) => setEditForm({ ...editForm, customer_name: e.target.value })}
                      data-testid="edit-claim-customer"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-phone">Phone</Label>
                    <Input
                      id="edit-phone"
                      value={editForm.phone}
                      onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                      data-testid="edit-claim-phone"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-amt">Order amount</Label>
                    <Input
                      id="edit-amt"
                      type="number"
                      step="0.01"
                      value={editForm.order_amount}
                      onChange={(e) => setEditForm({ ...editForm, order_amount: e.target.value })}
                      data-testid="edit-claim-amount"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-plat">Platform</Label>
                    <Input
                      id="edit-plat"
                      value={editForm.platform}
                      onChange={(e) => setEditForm({ ...editForm, platform: e.target.value })}
                      data-testid="edit-claim-platform"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="edit-notes">Notes</Label>
                  <Textarea
                    id="edit-notes"
                    rows={2}
                    value={editForm.notes}
                    onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                    data-testid="edit-claim-notes"
                  />
                </div>

                <div className="border-t border-border pt-3">
                  <div className="flex items-center justify-between mb-2">
                    <Label>Items</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addEditItem}
                      data-testid="edit-claim-add-item"
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add item
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {editItems.map((row) => (
                      <div key={row.uid} className="grid grid-cols-12 gap-2 items-start" data-testid={`edit-claim-item-${row.uid}`}>
                        <div className="col-span-7">
                          <SkuCombobox
                            skus={skus}
                            value={row.sku_id}
                            onChange={(v) => updateEditItem(row.uid, { sku_id: v })}
                          />
                        </div>
                        <div className="col-span-3">
                          <Input
                            type="number"
                            min="1"
                            value={row.quantity}
                            onChange={(e) => updateEditItem(row.uid, { quantity: e.target.value })}
                            placeholder="Qty"
                          />
                        </div>
                        <div className="col-span-2">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeEditItem(row.uid)}
                            disabled={editItems.length <= 1}
                            title="Remove"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditing(null)} disabled={editSaving}>
                  Cancel
                </Button>
                <Button onClick={saveEdit} disabled={editSaving} data-testid="edit-claim-save">
                  {editSaving ? "Saving…" : "Save changes"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
